import { DownloadOutlined, UploadOutlined } from "@ant-design/icons";
import { Alert, Button, List, Tag, Typography, message } from "antd";
import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";
import {
  IBusinessTmaVersion,
  listBusinessTmasFromServer,
  uploadBusinessTmaOnServer,
} from "../../businessesApi";

export default function BusinessTmaVersions({
  businessId,
  active,
  canUpload,
}: {
  businessId?: string;
  active: boolean;
  canUpload: boolean;
}) {
  const fileInput = useRef<HTMLInputElement>(null);
  const [rows, setRows] = useState<IBusinessTmaVersion[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string>();
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    if (!active || !businessId) return;
    let cancelled = false;
    setLoading(true);
    setError(undefined);
    listBusinessTmasFromServer(businessId)
      .then((next) => {
        if (!cancelled) setRows(next);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(_messageFrom(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [active, businessId, reloadToken]);

  const onFile = async (file: File) => {
    if (!businessId) return;
    if (
      file.type !== "application/pdf" &&
      !file.name.toLowerCase().endsWith(".pdf")
    ) {
      message.error("TMA upload must be a PDF");
      return;
    }
    setUploading(true);
    try {
      await uploadBusinessTmaOnServer({
        businessId,
        file: await _readAsDataUrl(file),
      });
      message.success("TMA uploaded");
      setReloadToken((token) => token + 1);
    } catch (err) {
      message.error(_messageFrom(err));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div style={{ marginBottom: 16 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 8,
        }}
      >
        <Typography.Title level={5} style={{ margin: 0 }}>
          TMA contracts
        </Typography.Title>
        {canUpload && (
          <>
            <input
              ref={fileInput}
              aria-label="Upload TMA"
              type="file"
              accept="application/pdf,.pdf"
              hidden
              onChange={(event) => {
                const file = event.target.files?.[0];
                event.target.value = "";
                if (file) void onFile(file);
              }}
            />
            <Button
              icon={<UploadOutlined />}
              loading={uploading}
              onClick={() => fileInput.current?.click()}
            >
              Upload TMA
            </Button>
          </>
        )}
      </div>
      {error && (
        <Alert
          type="error"
          showIcon
          message={error}
          style={{ marginBottom: 8 }}
          action={
            <Button size="small" onClick={() => setReloadToken((n) => n + 1)}>
              Retry
            </Button>
          }
        />
      )}
      <List
        size="small"
        loading={loading}
        dataSource={rows}
        locale={{ emptyText: "No TMA uploaded" }}
        renderItem={(row) => (
          <List.Item
            actions={[
              <Button
                key="download"
                type="link"
                icon={<DownloadOutlined />}
                aria-label={`Download ${row.fileName || row.id}`}
                disabled={!row.downloadUrl}
                onClick={() =>
                  window.open(row.downloadUrl, "_blank", "noopener,noreferrer")
                }
              >
                Download
              </Button>,
            ]}
          >
            <List.Item.Meta
              title={
                <span>
                  <Tag color={row.status === "active" ? "green" : "default"}>
                    {row.status === "active" ? "Active" : "Inactive"}
                  </Tag>
                  {row.fileName || row.id}
                </span>
              }
              description={
                <Typography.Text type="secondary">
                  {row.uploadedAt
                    ? dayjs(row.uploadedAt).format("DD/MM/YYYY HH:mm")
                    : "—"}
                  {" · "}
                  {row.uploadedBy || "—"}
                </Typography.Text>
              }
            />
          </List.Item>
        )}
      />
    </div>
  );
}

function _readAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

function _messageFrom(err: unknown): string {
  const data = (err as { response?: { data?: { message?: string } } }).response
    ?.data;
  if (data?.message) return data.message;
  if (err instanceof Error && err.message) return err.message;
  return "Request failed";
}
